import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError, selectTenant, switchTenant as apiSwitchTenant } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((r) => {
        if (mounted) setUser(r.data);
      })
      .catch(() => {
        if (mounted) setUser(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password, remember = false) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      if (data.needs_selection) {
        // Drop any leftover session token so it can't shadow the select_token.
        localStorage.removeItem("wd_token");
        return { ok: true, needsSelection: true, selectToken: data.select_token, memberships: data.memberships };
      }
      if (data.token) localStorage.setItem("wd_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const selectTenantSession = async (tenantId, selectToken, remember = false) => {
    const data = await selectTenant(tenantId, selectToken, remember);
    localStorage.setItem("wd_token", data.token);
    setUser(data.user);
    return data;
  };

  const switchTenant = async (tenantId) => {
    const data = await apiSwitchTenant(tenantId);
    localStorage.setItem("wd_token", data.token);
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.token) localStorage.setItem("wd_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const applySession = (data) => {
    if (data.token) localStorage.setItem("wd_token", data.token);
    setUser(data.user);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    localStorage.removeItem("wd_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, setUser, applySession, selectTenantSession, switchTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
